import { useEffect, useState } from "react";
import { fetchCommunicationChannels, fetchCustomerCommunicationDefault, updateCustomerCommunicationDefault } from "../../models/shared/communicationChannelsModel";

export function useCustomerCommunication({ customer, open, message }) {
  const [communicationChannels, setCommunicationChannels] = useState([]);
  const [customerCommunicationDefault, setCustomerCommunicationDefault] = useState(null);
  const [communicationLoading, setCommunicationLoading] = useState(false);
  const [communicationSaving, setCommunicationSaving] = useState(false);
  const [creationChannelOpen, setCreationChannelOpen] = useState(false);
  const [pendingCreateAction, setPendingCreateAction] = useState(null);

  const loadCommunicationSettings = async () => {
    if (!customer?.customer_id) return;
    setCommunicationLoading(true);
    try {
      const [catalogue, current] = await Promise.all([
        fetchCommunicationChannels(),
        fetchCustomerCommunicationDefault(customer.customer_id),
      ]);
      setCommunicationChannels(catalogue || []);
      setCustomerCommunicationDefault(current || null);
    } catch (error) {
      message.error(error?.response?.data?.message || error.message || "Could not load communication channels");
    } finally { setCommunicationLoading(false); }
  };

  useEffect(() => { if (open && customer?.customer_id) loadCommunicationSettings(); }, [open, customer?.customer_id]); // eslint-disable-line


  async function saveCustomerCommunicationDefault(nextKey) {
    if (!customer?.customer_id || !nextKey) return;
    setCommunicationSaving(true);
    try {
      const updated = await updateCustomerCommunicationDefault(customer.customer_id, {
        communication_channel_key: nextKey,
        ...(customerCommunicationDefault?.version ? { expected_version: customerCommunicationDefault.version } : {}),
      });
      setCustomerCommunicationDefault(updated);
      message.success("Default communication channel updated");
    } catch (error) {
      if (error?.response?.status === 409) {
        await loadCommunicationSettings();
        message.warning(error?.response?.data?.message || "The default changed elsewhere. The latest value has been loaded.");
      } else message.error(error?.response?.data?.message || error.message || "Could not update the default channel");
    } finally { setCommunicationSaving(false); }
  }



  // action: "draft" (Save Draft) | "send" (Create Query — creates a draft,
  // then the parent opens Send to Suppliers, same as it always has) |
  // "skip" (Create & Skip Suppliers — creates directly in waiting_for_price
  // via the backend's new status/reason support, no send modal at all).
  // "send" deliberately still creates as a plain draft rather than
  // requesting waiting_for_price itself: that status only becomes real
  // once suppliers are actually contacted (or "skip" is used instead) —
  // claiming it upfront would need a reason that doesn't make sense for
  // "I'm about to send this," and would trip the backend's new validation
  // for nothing.
  function handleCreateQuery(action = "draft") {
    setPendingCreateAction(action);
    setCreationChannelOpen(true);
  }


  const continueCreateQuery = async (selection, executeCreateQuery) => {
    const action = pendingCreateAction || "draft";
    setCreationChannelOpen(false);
    setPendingCreateAction(null);
    await executeCreateQuery(action, selection);
  };
  return { communicationChannels, customerCommunicationDefault, communicationLoading, communicationSaving, creationChannelOpen,
    pendingCreateAction, loadCommunicationSettings, saveCustomerCommunicationDefault, handleCreateQuery, continueCreateQuery };
}
